/* istanbul ignore file */
/* eslint object-shorthand: "off"*/
import _ from 'lodash';
import { COLORS_PALLETE_BY_RESOURCE_TYPE } from './graph.palletes';
import { renderToString } from 'react-dom/server';
import Tooltip from './tooltips/Tooltip';
import useGraphCategories from './useGraphCategories';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import useRaCostSetting from '../../settings/hooks/useRaCostSetting';
import { RESOURCE_TYPES } from '../../resource_types';
import { useContext } from 'react';
import ResourceAllocationContext from '../../state/context';
import { orderGraphDataByKey } from './graph.views';
import { CHART_LEGEND_ITEM_STYLE } from 'components/base/common-styles';
import PartialIndicator from '../PartialIndicator';
import { generateResourceColorKey } from '../../state/hooks/useRaColorAssigner';

export const GRAPH_OTHERS_KEY = 'others';

const DEFAULT_PALLETE =
  COLORS_PALLETE_BY_RESOURCE_TYPE[RESOURCE_TYPES.JIRA_PROJECTS.key];

const MAX_COL_KEY_LENGTH = 50;
const getColKeyInView = colKey =>
  colKey?.length > MAX_COL_KEY_LENGTH
    ? `${colKey.substring(0, MAX_COL_KEY_LENGTH)}...`
    : colKey;

const getLegendTitle = (item = {}) => {
  const { key, name } = item;
  if (!key || key === name) {
    return getColKeyInView(name);
  }
  const title = `${item?.key} ${item?.name ? '- ' + item?.name : ''}`;
  return getColKeyInView(title);
};

const useGraph = ({ data, resourceType, granularity, graphView }) => {
  const { colorAssigner } = useContext(ResourceAllocationContext);

  const raCost = useRaCostSetting();
  const categories = useGraphCategories({ data, granularity });
  const isPartialCategoryExists = categories.some(
    category => category.isPartial
  );
  const graphViewConfig = graphView.getConfig();
  const colorPallete =
    COLORS_PALLETE_BY_RESOURCE_TYPE[resourceType.key] ||
    COLORS_PALLETE_BY_RESOURCE_TYPE[resourceType.type] ||
    DEFAULT_PALLETE;
  colorAssigner.switchPalette(colorPallete);

  const orderedDataByFte = orderGraphDataByKey(data, 'effort_fte');

  const series = orderedDataByFte.map((item, idx) => {
    const colKey = _.get(item, 'key') || _.get(item, 'name') || '';
    const colKeyInView = getLegendTitle(item);
    const isOthers = colKey.toLowerCase() === GRAPH_OTHERS_KEY;

    const colorKey = generateResourceColorKey(resourceType.key, colKey);
    const color = colorAssigner.assignColor(colorKey);
    return {
      name: colKeyInView,
      data: _.get(item, 'allocation_data', []).map(point => ({
        y: graphViewConfig.getValue({ point, raCost }),
        extraData: point,
        isOthers
      })),
      color,
      visible: !isOthers
    };
  });

  const graphOptions = {
    chart: {
      type: 'column',
      style: {
        fontFamily: 'Outfit'
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      categories,
      labels: {
        useHTML: true,
        formatter() {
          const category = this.value;
          const { title, isPartial } = category;
          return `${title} ${
            isPartial ? renderToString(<PartialIndicator />) : ''
          }`;
        }
      }
    },
    yAxis: {
      min: 0,
      max: graphViewConfig.yAxis?.max || null,
      labels: {
        formatter: function () {
          return graphViewConfig.yAxis.labelFormatter({ label: this, raCost });
        }
      },
      title: ''
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      y: 0,
      navigation: {
        animation: true,
        arrowSize: 14
      },
      itemMarginBottom: 10,
      itemStyle: CHART_LEGEND_ITEM_STYLE
    },
    plotOptions: {
      column: {
        stacking: graphViewConfig.stacking,
        borderColor: null
      },
      series: {
        maxPointWidth: 40,
        cursor: 'pointer',
        point: {
          // tooltip onClick instead of onHover
          events: {
            click: function () {
              this.series.chart.update({
                tooltip: {
                  enabled: true
                }
              });
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.RESOURCE_ALLOCATION,
                click_type:
                  TRACKING.CLICK.RESOURCE_ALLOCATION.ALLOCATION_OVER_TIME_CHART
                    .TOOLTIP,
                value: resourceType.key
              });
            },
            mouseOut: function () {
              this.series.chart.update({
                tooltip: {
                  enabled: false
                }
              });
            }
          }
        }
      }
    },
    series,
    tooltip: {
      useHTML: true,
      backgroundColor: '#FFF',
      stickOnContact: true,
      borderColor: getColor('botticelli'),
      enabled: false,
      outside: true,
      formatter: /* istanbul ignore next */ function () {
        return renderToString(
          <Tooltip event={this} resourceType={resourceType} raCost={raCost} />
        );
      },
      style: {
        zIndex: 100
      }
    },
    credits: {
      enabled: false
    }
  };

  return {
    graphOptions,
    isPartialCategoryExists
  };
};

export default useGraph;
